import { classNames } from '@dimjs/utils';
import { hooks } from '@wove/react';
import { FC, ReactNode, useRef } from 'react';
import { useEffectCustom } from '../hooks/use-effect-custom';
import { CommonPropsWithChildren } from '../utils';
import './style.less';

export type NoticeBarProps = CommonPropsWithChildren & {
  prefix?: ReactNode;
  extra?: ReactNode;
  /**
   * 滚动速度，单位 px/s
   * 需要滚动的总宽度/speed 为滚动时长
   */
  speed?: number;
  loop?: boolean;
  onAnimatingEnd?: () => void;
  /**
   * 是否自动开始滚动，默认值true
   */
  autoStart?: boolean;
  /**
   * 开始滚动的延迟，单位 ms
   * autoStart=false，无效
   */
  delay?: number;
  /**
   * 受控状态
   */
  controlledStatus?: 'start' | 'stop';
  /**
   * 内容未达到最大宽度，显示时长，单位 ms
   * 时间到会调用onAnimatingEnd方法
   */
  duration?: number;
};

/**
 * 通知栏
 *
 * ```
 * 默认值设置
 * delay: 2000
 * autoStart: true,
 * loop: true,
 * // 滚动速度，单位 px/s（需要滚动的总宽度/speed）
 * speed: 50,
 * duration: 2000
 * ```
 */
export const NoticeBar: FC<NoticeBarProps> = (props) => {
  const classPrefix = 'v-note-bar';
  const contentWrapperRef = useRef<HTMLDivElement>(null);
  const contentRef = useRef<HTMLDivElement>(null);

  const animatingRef = useRef(false);

  const speed = props.speed as number;
  const duration = props.duration as number;
  const delay = props.delay as number;

  const start = hooks.useCallbackRef(() => {
    const contentWrapper = contentWrapperRef.current;
    const content = contentRef.current;
    if (!contentWrapper || !content) return;

    if (contentWrapper.offsetWidth >= content.offsetWidth) {
      animatingRef.current = false;
      setTimeout(() => {
        props.onAnimatingEnd?.();
      }, duration);
      stop();
      return;
    }

    if (animatingRef.current) return;

    const initial = !content.style.transform;
    content.style.transitionDuration = '0s';
    if (initial) {
      content.style.transform = 'translateX(0)';
    } else {
      content.style.transform = `translateX(${contentWrapper.offsetWidth}px)`;
    }
    const distance = initial ? content.offsetWidth : contentWrapper.offsetWidth + content.offsetWidth;
    animatingRef.current = true;
    content.style.transitionDuration = `${Math.round(distance / speed)}s`;
    content.style.transform = `translateX(-${content.offsetWidth}px)`;
  });

  const stop = hooks.useCallbackRef(() => {
    if (contentRef.current) {
      contentRef.current.style.removeProperty('transition-duration');
      contentRef.current.style.removeProperty('transform');
    }
  });

  hooks.useTimeout(() => {
    if (props.autoStart) {
      start();
    }
  }, delay);

  const onTransitionEnd = hooks.useCallbackRef(() => {
    props.onAnimatingEnd?.();
    animatingRef.current = false;
    if (props.loop) {
      start();
    } else {
      if (!props.controlledStatus) {
        stop();
      }
    }
  });

  useEffectCustom(() => {
    if (props.controlledStatus === 'start') {
      start();
    }
    if (props.controlledStatus === 'stop') {
      animatingRef.current = false;
      stop();
    }
  }, [props.controlledStatus]);

  return (
    <div className={classNames(classPrefix, props.className)} style={props.style}>
      {props.prefix ? <div className={`${classPrefix}-prefix`}>{props.prefix}</div> : null}
      <div className={`${classPrefix}-content-wrapper`} ref={contentWrapperRef}>
        <div className={`${classPrefix}-content`} onTransitionEnd={onTransitionEnd} ref={contentRef}>
          {props.children}
        </div>
      </div>
      {props.prefix ? <div className={`${classPrefix}-extra`}>{props.extra}</div> : null}
    </div>
  );
};

NoticeBar.defaultProps = {
  loop: true,
  autoStart: true,
  delay: 2000,
  duration: 2000,
  speed: 50,
};
